import { FrameworkIcon } from "@/components/frameworkIcon/FrameworkIcon";
import { SliderController } from "@/components/inspector/controller/SliderController";
import useVaultFilterStore from "@/stores/zustand/vault/useVaultFilterStore";
import { Framework7Icons } from "@/types/framework7-icons";
import {
  FilterBackgroundName,
  FilterColorBackground,
  FilterImageBackground,
  FilterName,
  FilterNameType,
  VaultObjectFilterControllableFactor,
} from "@/types/vault-object-filter-types";
import FilterPreset, {
  LightPresetName,
  LightPresetNameType,
  MuseumLightFactorSnap,
  VaultObjectFilterLightsFunction,
} from "@/utils/filterPreset";
import HENGE from "@henge-inc/global-types";
import HUI from "@henge-inc/ui/v1_2";
import { ICON as ICON_v1_1, i as i_v1_1 } from "@henge-inc/ui/v1_1";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { ColorPicker } from "./ColorPicker";
import { ControlFactor } from "./ControlFactor";
import { EffectButton } from "./EffectButton";
import { VaultObjectFilterItem } from "./VaultObjectFilterItem";
import debounce from "lodash/debounce";
import { onEscape } from "@/hooks/useEscape";

export const VaultObjectFilterController = () => {
  const currentFilter = useVaultFilterStore((state) => state.currentFilter);
  const { changeFilter, updateFilterValue, resetFilterValue } =
    useVaultFilterStore((state) => state.actions);
  const [controlFactor, setControlFactor] =
    useState<VaultObjectFilterControllableFactor | null>(null);

  useEffect(() => {
    if (!controlFactor) return;

    const sub = onEscape(
      () => {
        setControlFactor(null);
        return true;
      },
      { allowInEditable: true },
    );

    return () => sub.off();
  }, [controlFactor]);

  const [selectedBackgroundIndex, setSelectedBackgroundIndex] = useState(
    Object.values(FilterBackgroundName).findIndex(
      (type) => type === currentFilter.background.type,
    ),
  );
  useEffect(() => {
    const backgroundType =
      Object.values(FilterBackgroundName)[selectedBackgroundIndex];

    updateFilterValue("background", {
      type: backgroundType,
      hex: (currentFilter.background as FilterColorBackground).hex,
      url: (currentFilter.background as FilterImageBackground).url,
    });
  }, [selectedBackgroundIndex]);

  useEffect(() => {
    setSelectedBackgroundIndex(
      Object.values(FilterBackgroundName).findIndex(
        (type) => type === currentFilter.background.type,
      ),
    );
  }, [currentFilter.name]);

  const [debouncedColorValue, setDebouncedColorValue] = useState(
    (currentFilter.background as FilterColorBackground).hex,
  );
  const debounceColor = useMemo(
    () => debounce((value: string) => setDebouncedColorValue(value), 100),
    [],
  );

  const handleBackgroundColorChange = (value: string) => {
    if (currentFilter.background.type !== FilterBackgroundName.Color) {
      return;
    }

    debounceColor(value);
  };

  useEffect(() => {
    if (currentFilter.background.type !== FilterBackgroundName.Color) {
      return;
    }

    updateFilterValue("background", {
      type: FilterBackgroundName.Color,
      hex: debouncedColorValue,
    });
  }, [debouncedColorValue]);

  const [selectedLightPresetIndex, setSelectedLightPresetIndex] = useState(
    Object.values(LightPresetName).indexOf(currentFilter.lightPreset.name),
  );
  const [lightPresetName, setLightPresetName] = useState<LightPresetNameType>(
    currentFilter.lightPreset.name,
  );
  useEffect(() => {
    setLightPresetName(
      Object.values(LightPresetName)[selectedLightPresetIndex],
    );
    setLightFactor(
      Object.values(LightPresetName)[selectedLightPresetIndex] ===
        LightPresetName.Museum
        ? 0.4
        : 0.5,
    );
  }, [selectedLightPresetIndex]);
  const [lightFactor, setLightFactor] = useState(
    currentFilter.lightPreset.factor,
  );
  useEffect(() => {
    setSelectedLightPresetIndex(
      Object.values(LightPresetName).indexOf(currentFilter.lightPreset.name),
    );
    setLightPresetName(currentFilter.lightPreset.name);

    setLightFactor(currentFilter.lightPreset.factor);
  }, [currentFilter]);
  useEffect(() => {
    updateFilterValue("lightPreset", {
      name: lightPresetName,
      factor: lightFactor,
      ...VaultObjectFilterLightsFunction[lightPresetName](lightFactor),
    });
  }, [lightPresetName, lightFactor]);

  const handleFilterSelect = (filterName: FilterNameType) => {
    changeFilter(filterName);
  };

  return (
    <div className="flex flex-col justify-center items-center gap-4">
      <div className="w-full h-full flex flex-col justify-center items-center gap-3">
        <div className="flex flex-row justify-center items-center gap-1 relative">
          <div className="flex justify-center items-center">
            <EffectButton
              factor={"strength"}
              isSelected={controlFactor === "strength"}
              icon={
                <FrameworkIcon
                  icon={Framework7Icons.CameraFilters}
                  className={clsx(
                    "text-lg",
                    controlFactor === "strength"
                      ? "text-[var(--indigo100)]"
                      : "text-[var(--text-tertiary)]",
                  )}
                />
              }
              onClick={() =>
                setControlFactor((current) =>
                  current === "strength" ? null : "strength",
                )
              }
              onClose={() => setControlFactor(null)}
            >
              <ControlFactor isSelected={controlFactor === "strength"}>
                <div className="flex w-full items-center gap-3 p-3">
                  <HUI.Text
                    kind={HENGE.TEXT_KIND.small}
                    weight={HENGE.TEXT_WEIGHT.medium}
                    colour={HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]}
                    whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
                  >
                    Strength
                  </HUI.Text>
                  <SliderController
                    min={0}
                    max={1}
                    step={0.1}
                    value={currentFilter.strength}
                    setValue={(strength) => {
                      updateFilterValue("strength", strength);
                    }}
                  />
                  <HUI.Button
                    kind={HENGE.BUTTON_KIND.borderless}
                    size={HENGE.DETAILED_SIZE.xSmall}
                    colour={HENGE.BUTTON_COLOUR.gray}
                    onClick={() => {
                      resetFilterValue("strength");
                    }}
                    icon={{
                      icon: ICON_v1_1.RotateCcw,
                      size: HENGE.DETAILED_SIZE.xSmall,
                      kind: HENGE.ICON_KIND.regular,
                    }}
                  />
                </div>
              </ControlFactor>
            </EffectButton>
          </div>
          <div className="flex justify-center items-center">
            <EffectButton
              factor={"background"}
              isSelected={controlFactor === "background"}
              icon={
                <FrameworkIcon
                  icon={Framework7Icons.Pano}
                  className={clsx(
                    "text-lg",
                    controlFactor === "background"
                      ? "text-[var(--indigo100)]"
                      : "text-[var(--text-tertiary)]",
                  )}
                />
              }
              onClick={() =>
                setControlFactor((current) =>
                  current === "background" ? null : "background",
                )
              }
              onClose={() => setControlFactor(null)}
            >
              <ControlFactor isSelected={controlFactor === "background"}>
                <div className="flex flex-col w-full">
                  <div className="w-full p-2">
                    {/*<HUI.SegmentControl*/}
                    {/*  type="titleOnly"*/}
                    {/*  size={HENGE.DETAILED_SIZE.small}*/}
                    {/*  items={[{ title: "Color" }, { title: "Image" }]}*/}
                    {/*  fullWidth*/}
                    {/*  selectedIndex={selectedBackgroundIndex}*/}
                    {/*  setSelectedIndex={setSelectedBackgroundIndex}*/}
                    {/*/>*/}
                  </div>
                  {currentFilter.background.type ===
                    FilterBackgroundName.Color && (
                    <>
                      <ColorPicker
                        value={currentFilter.background.hex}
                        onChange={handleBackgroundColorChange}
                      />
                      <div className="flex w-full items-center gap-2 p-2 pt-0">
                        <div className="w-14 pl-2">
                          <HUI.Text
                            kind={HENGE.TEXT_KIND.small}
                            weight={HENGE.TEXT_WEIGHT.medium}
                            colour={HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]}
                            whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
                          >
                            Color
                          </HUI.Text>
                        </div>
                        <HUI.Input
                          kind={HENGE.INPUT_KIND.filled}
                          size={HENGE.DETAILED_SIZE.small}
                          fullWidth
                          textStyleProps={{
                            weight: HENGE.TEXT_WEIGHT.medium,
                            kind: HENGE.TEXT_KIND.small,
                            colour:
                              HENGE.SEMANTIC_TEXT_COLOUR["text-secondary"],
                          }}
                          value={currentFilter.background.hex}
                          setValue={handleBackgroundColorChange}
                        />
                        <HUI.Button
                          kind={HENGE.BUTTON_KIND.borderless}
                          size={HENGE.DETAILED_SIZE.xSmall}
                          colour={HENGE.BUTTON_COLOUR.gray}
                          onClick={() => {
                            resetFilterValue("background");
                          }}
                          icon={{
                            icon: ICON_v1_1.RotateCcw,
                            size: HENGE.DETAILED_SIZE.xSmall,
                            kind: HENGE.ICON_KIND.regular,
                          }}
                          className="mx-1"
                        />
                      </div>
                    </>
                  )}
                </div>
              </ControlFactor>
            </EffectButton>
          </div>
          <div className="flex justify-center items-center">
            <EffectButton
              factor={"lightPreset"}
              isSelected={controlFactor === "lightPreset"}
              icon={
                <FrameworkIcon
                  icon={Framework7Icons.SunMax}
                  className={clsx(
                    "text-lg",
                    controlFactor === "lightPreset"
                      ? "text-[var(--indigo100)]"
                      : "text-[var(--text-tertiary)]",
                  )}
                />
              }
              onClick={() =>
                setControlFactor((current) =>
                  current === "lightPreset" ? null : "lightPreset",
                )
              }
              onClose={() => setControlFactor(null)}
            >
              <ControlFactor isSelected={controlFactor === "lightPreset"}>
                <div className="flex flex-col w-full items-center gap-2 p-2">
                  <div className="flex w-full items-center gap-2">
                    <div className="flex-1">
                      <HUI.SegmentControl
                        type="titleOnly"
                        size={HENGE.DETAILED_SIZE.small}
                        items={[
                          { title: "Studio" },
                          { title: "Outdoor" },
                          { title: "Museum" },
                        ]}
                        fullWidth
                        selectedIndex={selectedLightPresetIndex}
                        setSelectedIndex={setSelectedLightPresetIndex}
                      />
                    </div>
                  </div>
                  <div className="flex w-full items-center gap-3 py-1">
                    <SliderController
                      min={0}
                      max={1}
                      showTick={currentFilter.lightPreset.name === "Museum"}
                      step={
                        currentFilter.lightPreset.name === "Museum"
                          ? MuseumLightFactorSnap
                          : 0.1
                      }
                      value={lightFactor}
                      setValue={setLightFactor}
                    />
                    <HUI.Button
                      kind={HENGE.BUTTON_KIND.borderless}
                      size={HENGE.DETAILED_SIZE.xSmall}
                      colour={HENGE.BUTTON_COLOUR.gray}
                      onClick={() => {
                        resetFilterValue("lightPreset");
                      }}
                      icon={{
                        icon: ICON_v1_1.RotateCcw,
                        size: HENGE.DETAILED_SIZE.xSmall,
                        kind: HENGE.ICON_KIND.regular,
                      }}
                      className="mx-1"
                    />
                  </div>
                </div>
              </ControlFactor>
            </EffectButton>
          </div>
          <div className="flex justify-center items-center">
            <EffectButton
              factor={"camera"}
              isSelected={controlFactor === "camera"}
              icon={
                <FrameworkIcon
                  icon={Framework7Icons.Videocam}
                  className={clsx(
                    "text-lg",
                    controlFactor === "camera"
                      ? "text-[var(--indigo100)]"
                      : "text-[var(--text-tertiary)]",
                  )}
                />
              }
              onClick={() =>
                setControlFactor((current) =>
                  current === "camera" ? null : "camera",
                )
              }
              onClose={() => setControlFactor(null)}
            >
              <ControlFactor isSelected={controlFactor === "camera"}>
                <div className="flex w-full items-center gap-3 p-3">
                  <div className="flex w-full items-center gap-2 justify-between">
                    <div className="flex w-full items-center gap-2">
                      <div className="w-14 px-1 h-6 flex items-center">
                        <HUI.Text
                          kind={HENGE.TEXT_KIND.small}
                          weight={HENGE.TEXT_WEIGHT.medium}
                          colour={HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]}
                          whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
                        >
                          Fov
                        </HUI.Text>
                      </div>
                      <SliderController
                        min={1}
                        max={150}
                        step={1}
                        value={Math.max(1, currentFilter.camera.fov)}
                        setValue={(fov) => {
                          updateFilterValue("camera", {
                            ...FilterPreset[
                              currentFilter.name as FilterNameType
                            ].camera,
                            fov: Math.max(1, fov),
                          });
                        }}
                      />
                    </div>
                    <HUI.Button
                      kind={HENGE.BUTTON_KIND.borderless}
                      size={HENGE.DETAILED_SIZE.xSmall}
                      colour={HENGE.BUTTON_COLOUR.gray}
                      onClick={() => {
                        resetFilterValue("camera");
                      }}
                      icon={{
                        icon: ICON_v1_1.RotateCcw,
                        size: HENGE.DETAILED_SIZE.xSmall,
                        kind: HENGE.ICON_KIND.regular,
                      }}
                      className="mx-1"
                    />
                  </div>
                </div>
              </ControlFactor>
            </EffectButton>
          </div>
          <div className="flex justify-center items-center">
            <EffectButton
              factor={"turntable"}
              isSelected={controlFactor === "turntable"}
              icon={
                <i_v1_1.RotateBox
                  kind={HENGE.ICON_KIND.thin}
                  colour={
                    controlFactor === "turntable"
                      ? HENGE.MAIN_COLOUR.indigo100
                      : HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]
                  }
                  size={HENGE.DETAILED_SIZE.medium}
                />
              }
              onClick={() =>
                setControlFactor((current) =>
                  current === "turntable" ? null : "turntable",
                )
              }
              onClose={() => setControlFactor(null)}
            >
              <ControlFactor isSelected={controlFactor === "turntable"}>
                <div className="flex w-full items-center gap-3 p-3">
                  <div className="flex w-full items-center gap-2 justify-between flex-col">
                    <div className="flex w-full items-center gap-2 justify-between">
                      <div className="flex w-full items-center gap-2">
                        <div className="w-14 px-1 h-6 flex items-center">
                          <HUI.Text
                            kind={HENGE.TEXT_KIND.small}
                            weight={HENGE.TEXT_WEIGHT.medium}
                            colour={HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]}
                            whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
                          >
                            Speed
                          </HUI.Text>
                        </div>
                        <SliderController
                          min={0}
                          max={1}
                          step={0.1}
                          value={currentFilter.turntable?.speed ?? 0}
                          setValue={(turntableSpeed) => {
                            updateFilterValue("turntable", {
                              speed: turntableSpeed,
                              // showHengeTile: turntableShowHengeTile,
                              showHengeTile: false,
                            });
                          }}
                        />
                      </div>
                      <HUI.Button
                        kind={HENGE.BUTTON_KIND.borderless}
                        size={HENGE.DETAILED_SIZE.xSmall}
                        colour={HENGE.BUTTON_COLOUR.gray}
                        onClick={() => {
                          resetFilterValue("turntable");
                        }}
                        icon={{
                          icon: ICON_v1_1.RotateCcw,
                          size: HENGE.DETAILED_SIZE.xSmall,
                          kind: HENGE.ICON_KIND.regular,
                        }}
                        className="mx-1"
                      />
                    </div>
                  </div>
                </div>
              </ControlFactor>
            </EffectButton>
          </div>
        </div>

        <div className="w-full flex justify-center items-center gap-2">
          <VaultObjectFilterItem
            name={FilterName.Default}
            onSelect={handleFilterSelect}
          />
          <VaultObjectFilterItem
            name={FilterName.Jobs}
            onSelect={handleFilterSelect}
          />
          <VaultObjectFilterItem
            name={FilterName.Kubrick}
            onSelect={handleFilterSelect}
          />
          <VaultObjectFilterItem
            name={FilterName.Nolan}
            onSelect={handleFilterSelect}
          />
        </div>
      </div>
    </div>
  );
};
